import React, { useEffect, useRef, useState } from "react";

import packEventValue from "../../shared/packEventValue";

import { formatName } from "./CoverageTimeline";
import {
  MIN_PRINT_DPI,
  PRINT_DISTANCES,
  PRINT_INCHES,
  groundSideMiles,
  printScale,
} from "./printArea";

const HIGH_ALTITUDE_PRINT_FRAME_ID = 37;
const HIGH_ALTITUDE_PRINT_SIZE_ID = 26;
const PREVIEW_DEBOUNCE_MS = 350;

function formatDistance(distance) {
  switch (distance) {
    case 0.25:
      return "¼ mile";
    case 0.5:
      return "½ mile";
    case 0.75:
      return "¾ mile";
    case 1:
      return "1 mile";
    case 2.5:
      return "2½ miles";
    default:
      return `${distance} miles`;
  }
}

function formatMiles(miles) {
  if (miles < 1) return miles.toFixed(2);
  return miles < 10 ? miles.toFixed(1) : Math.round(miles);
}

function usePrintPreview(coverage, bbox) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const key = coverage && bbox ? `${coverage.path}/${bbox.join(",")}` : null;

  useEffect(() => {
    setPreview(null);
    setError(null);
    if (!key) return;

    let cancelled = false;
    setLoading(true);

    const timeout = setTimeout(() => {
      fetch(
        `https://va-tiller.b-cdn.net/${coverage.path}/preview/${bbox.join(",")}`,
      )
        .then((response) => {
          if (!response.ok)
            throw new Error(`Preview failed (${response.status})`);
          return response.json();
        })
        .then((data) => {
          if (!cancelled) setPreview(data);
        })
        .catch((e) => {
          if (!cancelled) setError(e.message);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [key]);

  return { preview, loading, error };
}

export default function PrintSelector({
  className,
  coverage,
  mode,
  onModeChange,
  distance,
  onDistanceChange,
  bbox,
  imageryCoverage,
  cartItemsPath,
  couponCode,
  onClose,
}) {
  const { preview, loading, error } = usePrintPreview(coverage, bbox);
  const [overrideResolution, setOverrideResolution] = useState(false);

  useEffect(
    () => setOverrideResolution(false),
    [coverage, bbox && bbox.join(",")],
  );

  const dpi = preview ? Math.round(preview.width / PRINT_INCHES) : null;
  const scale = bbox && printScale(bbox);
  const lowResolution = dpi !== null && dpi < MIN_PRINT_DPI;
  const canAdd = !!preview && (!lowResolution || overrideResolution);
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

  const hadPreview = useRef(false);
  const warnedLowResolution = useRef(false);
  if (preview) hadPreview.current = true;

  useEffect(() => {
    if (!lowResolution || warnedLowResolution.current) return;

    warnedLowResolution.current = true;
    _vaTrack("map.high_altitude_print_low_resolution", { event_value: dpi });
  }, [lowResolution]);

  // The panel unmounts when closed; adding to cart navigates away instead
  useEffect(
    () => () =>
      _vaTrack("map.high_altitude_print_closed", {
        event_value: hadPreview.current,
      }),
    [],
  );

  return (
    <div
      className={`bg-white rounded shadow-lg p-3 text-xs text-gray-700 ${className}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold text-sm text-gray-900">
          9&Prime;&times;9&Prime; High Altitude Print
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close print tool"
          className="text-gray-500 hover:text-gray-900 text-lg leading-none"
        >
          &times;
        </button>
      </div>

      {!coverage ? (
        <p className="mb-0">
          Choose a year from the imagery timeline to print it. Modern imagery
          isn&rsquo;t available as a print.
        </p>
      ) : (
        <>
          <div className="flex rounded border border-gray-300 overflow-hidden mb-2">
            {[
              ["point", "Point & radius"],
              ["box", "Draw a square"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => onModeChange(value)}
                className={`flex-1 py-1 ${
                  mode === value
                    ? "bg-va-blue-700 text-white"
                    : "bg-white hover:bg-gray-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === "point" ? (
            <div className="flex items-center gap-x-2 mb-2">
              <label htmlFor="print-distance" className="mb-0 font-normal">
                Distance
              </label>
              <select
                id="print-distance"
                value={distance}
                onChange={(e) => onDistanceChange(parseFloat(e.target.value))}
                className="p-1 text-xs"
              >
                {PRINT_DISTANCES.map((d) => (
                  <option key={d} value={d}>
                    {formatDistance(d)}
                  </option>
                ))}
              </select>
              <span className="text-gray-500">
                Drag the pin or square, or click the map
              </span>
            </div>
          ) : (
            <p className="mb-2 text-gray-500">
              {bbox
                ? "Drag the square to move it, or its edges to resize. Shift-drag to draw a new one."
                : "Drag on the map to draw a square."}
            </p>
          )}

          {bbox && (
            <div className="flex gap-x-3">
              <div className="w-24 h-24 shrink-0 bg-gray-100 flex items-center justify-center">
                {preview ? (
                  <img
                    src={preview.image}
                    alt="Print preview"
                    className="w-24 h-24 object-cover"
                  />
                ) : (
                  <span className="text-gray-400">
                    {error ? "No preview" : "Loading..."}
                  </span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900">
                  {formatName(coverage.name)}
                </div>
                <div>
                  {formatMiles(groundSideMiles(bbox))} &times;{" "}
                  {formatMiles(groundSideMiles(bbox))} miles
                </div>
                <div>
                  Scale 1:{scale.ratio.toLocaleString()} (1 in &asymp;{" "}
                  {scale.perInch})
                </div>
                {preview && (
                  <div>
                    {preview.width}&times;{preview.height} ({dpi} dpi)
                  </div>
                )}
                {loading && !preview && (
                  <div className="text-gray-400">Checking resolution...</div>
                )}
                {error && <div className="text-red-600">{error}</div>}
                {imageryCoverage !== null && imageryCoverage < 0.95 && (
                  <div className="text-amber-700">
                    Part of this square has no {formatName(coverage.name)}{" "}
                    imagery.
                  </div>
                )}
              </div>
            </div>
          )}

          {lowResolution && (
            <div className="mt-2">
              <div className="text-red-600">
                Sorry, there isn&rsquo;t enough resolution for a quality print.
                Try a larger area or a different year.
              </div>
              <label className="flex items-center gap-x-1 mb-0 font-normal">
                <input
                  type="checkbox"
                  checked={overrideResolution}
                  onChange={(e) => {
                    if (e.target.checked) {
                      _vaTrack("map.high_altitude_print_override", {
                        event_value: dpi,
                      });
                    }
                    setOverrideResolution(e.target.checked);
                  }}
                />
                I understand, add anyway
              </label>
            </div>
          )}

          <form
            action={cartItemsPath}
            method="post"
            className="flex items-center justify-between mt-3 mb-0"
            onSubmit={() =>
              _vaTrack("map.high_altitude_print_add_to_cart", {
                event_value: packEventValue({
                  coverage: coverage.id,
                  mode,
                  side_miles: groundSideMiles(bbox).toFixed(2),
                  dpi,
                  override: lowResolution,
                }),
              })
            }
          >
            <input
              type="hidden"
              name="authenticity_token"
              value={csrfToken || ""}
            />
            <input
              type="hidden"
              name="raster_coverage_id"
              value={coverage.id}
            />
            <input
              type="hidden"
              name="raster_coverage_bounding_box"
              value={bbox ? bbox.join(",") : ""}
            />
            <input
              type="hidden"
              name="frame_id"
              value={HIGH_ALTITUDE_PRINT_FRAME_ID}
            />
            <input
              type="hidden"
              name="size_id"
              value={HIGH_ALTITUDE_PRINT_SIZE_ID}
            />
            {couponCode && (
              <input type="hidden" name="coupon_code" value={couponCode} />
            )}

            <span className="text-base font-bold text-gray-900">$49</span>
            <button
              type="submit"
              disabled={!canAdd}
              className="add-to-cart-button with-cart text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add to Cart
            </button>
          </form>
        </>
      )}
    </div>
  );
}
